import {DateTime} from 'luxon';
import {TimezoneObject} from 'common/src/modules/timezones/timezones.types';
import {CLEnum} from 'common/src/modules/enums/enums.types';
import {ProcessScreensStatus} from '../doohScreens/doohScreens.types';
import {RemoteFont} from '../fonts/fonts.types';
import {CampaignSubType, CampaignType} from '../campaigns/campaigns.types';
import {IntersectionUser} from '../userClients/userClients.types';

enum JobType {
  DynamicReportJob = 'DynamicReportJob',
  EnrichFeedFileJob = 'EnrichFeedFileJob',
  UpdateLocationFeedJob = 'UpdateLocationFeedInventoryJob',
  UpdateFeedInventoryJob = 'UpdateFeedInventoryJob',
  UpdateFeedRelatedItemsJob = 'UpdateFeedRelatedItemsJob',
  AePublishJob = 'AePublishJob',
  ProcessOfflineEventsJob = 'ProcessOfflineEventsJob',
  TearsheetExportJob = 'TearsheetToPdfExportJob',
  UpdateLocationSetItemsJob = 'UpdateLocationSetItemsJob',
  GenerateAdsForCampaignsJob = 'GenerateAdsForCampaignsJob',
  FeedFileDPAEnrichmentJob = 'FeedFileDPAEnrichmentJob',
}

enum JobStatus {
  Success = 'Success',
}

enum JobUIStatus {
  Unkown = 0,
  Enqueued = 10,
  Started = 20,
  Aborted = 25,
  Completed = 30,
  Failed = 40,
}

enum JobStage {
  Complete = 'Completed',
  Failed = 'Failed',
  Aborted = 'Aborted',
}

enum JobResultType {
  Template = 'template',
  Creative = 'creative',
  Widget = 'widget',
  FeedInventory = 'feedInventoryUpdate',
  ExportFeedItems = 'exportFeedItems',
  LocationFeed = 'locationFeedInventoryUpdate',
  DownloadMedia = 'downloadMedia',
  Tearsheet = 'tearsheet',
  ValidateDOOHScreens = 'validateDOOHScreens',
  Message = 'message',
  AddFontToFontsLibrary = 'AddFontToFontsLibrary',
  ImportCustomParams = 'importCustomParams',
  Reports = 'reports',
  ContentSet = 'contentSet',
  HealthCheck = 'healthCheck',
  SocialTearsheet = 'socialTearsheet',
}

interface JobResultData {
  id: number;
  type: JobResultType;
}

interface JobWidgetResult extends JobResultData {
  type: JobResultType.Widget;
}

interface JobTemplateResult extends JobResultData {
  type: JobResultType.Template;
}

interface JobCreativeResult extends JobResultData {
  type: JobResultType.Creative;
}

interface JobFeedItemsResult extends JobResultData {
  type: JobResultType.FeedInventory;
}

interface JobExportFeedItemsResult extends JobResultData {
  type: JobResultType.ExportFeedItems;
  fileName: string;
  url: string;
}

interface JobLocationFeedItemsResult extends JobResultData {
  type: JobResultType.LocationFeed;
}

interface JobDownloadUrl {
  url: string;
  type: JobResultType.DownloadMedia;
}

interface JobTearsheetResult extends JobResultData {
  type: JobResultType.Tearsheet;
  campaignId: number;
  versionId: number;
  campaignType: CampaignType;
  campaignSubType: CampaignSubType;
}

interface JobValidateDOOHScreensResult extends JobResultData {
  type: JobResultType.ValidateDOOHScreens;
  reportPath: string;
  status: ProcessScreensStatus;
}

interface JobMessageResult {
  type: JobResultType.Message;
  message: string;
}

interface AddFontJobResult {
  type: JobResultType.AddFontToFontsLibrary;
  message: string;
  fonts: RemoteFont[];
}

interface ImportTemplateJobResult {
  type: JobResultType.Template;
  id: number;
  templateName: string;
}

interface ImportCustomParamsJobResult {
  type: JobResultType.ImportCustomParams;
  failed: number;
  succeeded: number;
  reportUrl: string;
}

interface JobReportResult {
  type: JobResultType.Reports;
  url: string;
}

interface JobContentSetResult {
  type: JobResultType.ContentSet;
  contentSetId: number;
}

interface JobHealthCheckResult {
  type: JobResultType.HealthCheck;
  url: string;
}

type JobResult =
  | JobTemplateResult
  | JobCreativeResult
  | JobFeedItemsResult
  | JobExportFeedItemsResult
  | JobLocationFeedItemsResult
  | JobDownloadUrl
  | JobTearsheetResult
  | JobWidgetResult
  | JobValidateDOOHScreensResult
  | JobMessageResult
  | AddFontJobResult
  | ImportCustomParamsJobResult
  | JobReportResult
  | JobContentSetResult
  | JobHealthCheckResult;

interface JobUI<Result extends JobResult> {
  status: JobUIStatus;
  result: Result;
  message: string;
  Infos: string[];
  warnings: string[];
  errors: string[];
}

interface ScheduleJobTiming {
  cron: string;
  cronDescriptor: string;
  cronNextRun: string;
  cronLastRun: string;
  timeZone: TimezoneObject;
  IsValid: boolean;
}

interface ScheduledJobExecuteResponse {
  code: number;
  codeUI: string;
  message: string;
  reportUrl: string;
  jobStats: {
    Counters: Record<string, number>;
    RecordsLists: Record<string, string>;
    Infos: string[];
    Warnings: string[];
    Errors: string[];
  };
  infos: [];
  warnings: [];
  errors: [];
}

interface RemoteScheduledJob {
  id: number;
  name: string;
  description: string;
  jobType: string;
  jobStr: string;
  lastEnqueueTime: string;
  lastExecStatus: string;
  lastExecTime?: string;
  status: number;
  timing: ScheduleJobTiming;
  lastExecResp: ScheduledJobExecuteResponse;
  ref_obj_1: string;
}

interface CreativeIdFormat {
  creativeId: number;
  adSize: string;
}

interface RemoteScheduledJobData {
  dynamicReportId: number;
  parameterValuesDic: Record<string, string>;
  fromDate: string;
  toDate: string;
  relativeDuration: number;
  emailRecipients: {$values: string[]};
  customLastDays: number;
  isCustom: boolean;
  creativeIdsAndFormats?: {$values: CreativeIdFormat[]};
  campaignIds: {$values: number[]};
}

interface ScheduledJobData
  extends Omit<
    RemoteScheduledJobData,
    'emailRecipients' | 'fromDate' | 'toDate' | 'date' | 'creativeIdsAndFormats' | 'campaignIds'
  > {
  recipients: string;
  dateRange: string;
  fromDate: DateTime;
  toDate: DateTime;
  creativeIdsAndFormats?: string;
  campaignIds: string;
  id?: number;
}

interface ScheduledJob
  extends Omit<
    RemoteScheduledJob,
    'jobType' | 'jobStr' | 'lastEnqueueTime' | 'lastExecTime' | 'timing'
  > {
  jobType: JobType;
  jobData: ScheduledJobData;
  lastEnqueueTime: DateTime;
  lastExecTime: DateTime;
  schedule: string;
  nextRun: DateTime;
}

interface TemplateParameters {
  type: number;
  optionsIdToValueDic: Record<number, string>;
  clinchName: string;
  isAggregated: boolean;
  isRequired: boolean;
}

interface RemoteReportTemplate {
  reportName?: string;
  headers?: string[];
  metrics?: string[];
  filters?: string[];
  selectedReportTemplate?: number;
  limitTo?: number;
  relativeDuration?: number;
  customLastDays?: number;
  startDate?: string;
  endDate?: string;
  cronExpression?: string;
  timeZone?: string;
  reportTimeZone?: string;
  emailRecipients?: string[];
  parametersData?: Record<string, string[]>;
  outputType?: number;
  isCustom?: boolean;
  existingJobId?: number;
  templateParameters?: Record<string, TemplateParameters>;
  minimumImpressionsThreshold?: number;
  storageInfo?: {
    id: number;
    fileName: string;
  };
}

interface ReportTemplate extends Omit<RemoteReportTemplate, 'startDate' | 'endDate'> {
  startDate?: DateTime;
  endDate?: DateTime;
  useMinImpressions?: boolean;
}

enum JobRelativeDuration {
  FromDay = 50,
  LastDays = 40,
  DateRange = 0,
  PreviousMonth = 30,
  MonthToDate = 31,
}

interface AdditionalInfoStats {
  counters: Record<string, number>;
  messages: string[];
}

interface AdditionalInfo {
  message: string;
  reportUrl: string;
  initiator: string;
  statsSummary?: {
    infos: AdditionalInfoStats;
    warnings: AdditionalInfoStats;
    errors: AdditionalInfoStats;
  };
}

enum DynamicReportOutputType {
  Excel = 0,
  CSV = 1,
  CSV_GZIPPED = 2,
  PARQUET = 3,
}

enum DynamicReportOutputTypeName {
  GZIPPED_CSV = 'GZIPPED CSV',
  XSLX = 'XSLX',
  CSV = 'CSV',
  PARQUET = 'PARQUET',
}

interface RemoteJobStatus {
  jobId: string;
  stageStr: JobStage;
  progressPerc: number;
}

interface RemoteJobExecution {
  id: number;
  name: string;
  scheduledJobId: number;
  clientId: number;
  jobType: string;
  jobStr: string;
  enqueueTime?: string;
  startTime?: string;
  endTime?: string;
  execStatus?: number;
  comment: string;
  additionalInfo: string;
  reportUrl: string;
  isManual: boolean;
  ref_obj_1?: string;
  ref_obj_2?: string;
  ref_obj_3?: string;
  result?: string;
  reason?: string;
  initiatedByUser?: IntersectionUser;
  initiatedAtTime?: string;
}

interface JobExecution<JobData = ScheduledJobData>
  extends Omit<
    RemoteJobExecution,
    'startTime' | 'endTime' | 'enqueueTime' | 'execStatus' | 'additionalInfo' | 'initiatedAtTime'
  > {
  startTime: DateTime;
  endTime: DateTime;
  enqueueTime: DateTime;
  execStatus: CLEnum;
  executionType: string;
  additionalInfo: AdditionalInfo;
  jobData: JobData;
  initiatedAtTime: DateTime;
}

enum ScheduledJobStatus {
  Stopped = 0,
  Active = 1,
  Completed = 2,
  Errored = 3,
  NonScheduled = 999,
}

export interface ScheduledJobTiming {
  cronDescriptor: string;
  cron: string;
  timeZone: string;
}

interface JobExecutionParams {
  executionId: number;
}

enum JobExecutionStatus {
  Enqueued = 'Enqueued',
  Started = 'Started',
  Aborted = 'Aborted',
  Completed = 'Completed',
  Failed = 'Failed',
}

enum JobQueue {
  Normal = 'Normal',
  Immediate = 'Immediate',
  ResourceIntensive = 'ResourceIntensive',
  LowPriority = 'LowPriority',
}

export {
  JobType,
  JobStatus,
  JobStage,
  RemoteScheduledJobData,
  ScheduledJobData,
  RemoteScheduledJob,
  ScheduledJob,
  ReportTemplate,
  TemplateParameters,
  JobRelativeDuration,
  RemoteJobExecution,
  JobExecution,
  RemoteJobStatus,
  AdditionalInfo,
  CreativeIdFormat,
  DynamicReportOutputType,
  DynamicReportOutputTypeName,
  ScheduledJobStatus,
  JobUI,
  JobUIStatus,
  JobResult,
  JobResultType,
  JobTemplateResult,
  JobDownloadUrl,
  JobValidateDOOHScreensResult,
  JobExecutionParams,
  AddFontJobResult,
  ImportTemplateJobResult,
  ImportCustomParamsJobResult,
  JobContentSetResult,
  JobExecutionStatus,
  JobQueue,
};
